import React, { useEffect, useState } from 'react';

import { Input, Button, message, Checkbox, Tooltip } from 'antd';
import { Formik, ErrorMessage } from 'formik';
import { useHistory } from 'react-router-dom';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import TagsTransfer from './TagsTransfer';

import { updateUserDetail, updateUserAddress, getDocTypes } from '../../../hooks/users';
import { cpfMask } from '../../../utils/masks';
import CompanySelect from '../../molecules/CompanySelect';
import OperationsTransfer from '../OperationsTransfer';
import TrustedUserAuditHistory from './TrustedUserAuditHistory';
import { confirmTrustedUser } from './confirmTrustedUser';
import { QuestionIcon } from '../../atoms/icons/QuestionIcon';
import { COLORS } from '../../../utils/theme/colors';
import { hideTrustedUserButton } from '../../../utils/hideTrustedUserButton/hideTrustedUserButton';
import { canEditEmailPagBank } from '../../../utils/canEditEmailPagBank/canEditEmailPagBank';
import {
  INVALID_EMAIL_MESSAGE,
  isValidEmail,
  normalizeEmail,
} from '../../../utils/email/email';
import RiskScoreBadge from '../../atoms/RiskScoreBadge/RiskScoreBadge';
import CardRiskScoreBadge from '../../atoms/RiskScoreBadge/CardRiskScoreBadge';
import {
  getClearSaleCardScore,
  getClearSaleScore,
} from '../../../hooks/riskAnalysis';
import { hasPagBankCard } from '../../../hooks/pagSeguro';

const UserConfigFormWrapper = styled.div`
  form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    label {
      margin-right: 10px;
    }

    button {
      margin-top: 20px;
    }
  }
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: bold;
`;

const FormInputWrapper = styled.div`
  width: 100%;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: flex-start;

  select {
    padding: 4px 20px 4px 8px;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
    touch-action: manipulation;
    appearance: none;
    background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' standalone='no'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg t='1597601650772' class='icon' viewBox='0 0 1024 1024' version='1.1' xmlns='http://www.w3.org/2000/svg' p-id='1210' xmlns:xlink='http://www.w3.org/1999/xlink' width='16' height='16'%3E%3Cdefs%3E%3Cstyle type='text/css'%3E%3C/style%3E%3C/defs%3E%3Cpath d='M726.652801 429.305603 297.347199 429.305603 512.193405 638.156258Z' p-id='1211'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right;

    &:focus {
      border-color: #40a9ff;
      border-right-width: 1px !important;
      outline: 0;
      -webkit-box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }

    &:hover {
      border-color: #40a9ff;
      border-right-width: 1px !important;
    }
  }

  .docType {
    padding: 0px 20px 0px 8px;
  }

  @media (min-width: 1024px) {
    input:not([type='checkbox']) {
      min-width: 300px;
    }
  }

  input:hover,
  input:focus,
  input:active,
  input:disabled:hover,
  .ant-input-affix-wrapper:hover,
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper:active,
  .ant-input-affix-wrapper:visited,
  .ant-input-affix-wrapper-focused,
  select:hover,
  select:focus,
  select:active,
  textarea:hover,
  textarea:focus,
  textarea:active {
    border-right-width: 0 !important;
    border-color: #40a9ff;
    outline: none;
    box-shadow: none;
  }

  input:disabled:hover {
    border-color: rgba(0, 0, 0, 0.3);
  }

  .ant-input-affix-wrapper {
    border-right-width: 0 !important;
  }

  input,
  .ant-input-affix-wrapper,
  select,
  textarea {
    font-size: 14px;
    font-weight: normal;
    border-radius: 0 0 1px 0;
    border-width: 0 0 1px 0;
    border-color: #000;

    /* &:hover,
    &:focus,
    &:disabled:hover {
      border-right-width: 0 !important;
      box-shadow: none;
    } */
  }

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const FormInputGroupWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  width: 100%;

  > div:first-child {
    margin-right: 40px;
  }
`;

const CheckBoxDiv = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
  flex-direction: row;
`;

const MessageBox = styled.div`
  color: red;
  font-size: 12px;
`;
const InputField = styled.input`
  width: 342px;
`;

const CheckBoxThreeDSContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-left: 40px;
`;

const TooltipPositionWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledQuestionIconWrapper = styled.span`
  position: absolute;
  top: -4px;
  right: 12px;
  transform: translateY(-50%);
  cursor: pointer;
`;

const UserConfigForm = ({
  initialValues,
  type,
  tenants,
  userGroups,
  userPk = null,
  availableOcppTags,
  targetOcppTags,
  targetChargeboxes,
  isVehicle,
  companiesList,
  getCompaniesData,
  chargePointsList,
  isPagBank,
}) => {
  const [targetKeys, setTargetKeys] = useState(targetOcppTags);
  const [chargeboxKeys, setChargeboxKeys] = useState([]);
  const [scoreState, setScoreState] = useState({ loading: false, data: null });
  const [cardScoreState, setCardScoreState] = useState({
    loading: true,
    hasCard: null,
    data: null,
  });
  const [docTypeList, setDocTypeList] = useState([]);

  const history = useHistory();

  const user = JSON.parse(localStorage.getItem('auth-user'));
  const shouldShowNotificationOfflineCp = user?.permissions.some(
    (permission) => permission.route === '/users/notification_offline_cp',
  );

  useEffect(() => {
    (async function () {
      const data = await getDocTypes();
      setDocTypeList(data?.docTypeList || []);
    })();
  }, []);


  useEffect(() => {
    if (targetChargeboxes && targetChargeboxes.length > 0) {
      setChargeboxKeys(targetChargeboxes);
    } else {
      setChargeboxKeys([]);
    }
  }, [targetChargeboxes]);

  useEffect(() => {
    const document = initialValues?.docNumber;
    const documentType = initialValues?.docType;

    if (!document) {
      setScoreState({ loading: false, data: null });
      return undefined;
    }

    let active = true;
    setScoreState({ loading: true, data: null });

    getClearSaleScore(document, documentType).then((data) => {
      if (active) {
        setScoreState({ loading: false, data });
      }
    });

    return () => {
      active = false;
    };
  }, [initialValues?.docNumber, initialValues?.docType]);

  // MV2-2053: efeito separado do score do usuário para que um nunca afete o outro.
  // "Tem cartão" vem do PagBank (cadastro atual); o score vem do TrustCard da ClearSale.
  // As duas funções nunca rejeitam (erro vira null), então o Promise.all não perde nenhuma.
  useEffect(() => {
    const userUuid = initialValues?.uuid;
    const document = initialValues?.docNumber;
    const documentType = initialValues?.docType;

    let active = true;
    setCardScoreState({ loading: true, hasCard: null, data: null });

    Promise.all([
      hasPagBankCard(userUuid),
      getClearSaleCardScore(document, documentType),
    ]).then(([hasCard, data]) => {
      if (active) {
        setCardScoreState({ loading: false, hasCard, data });
      }
    });

    return () => {
      active = false;
    };
  }, [initialValues?.uuid, initialValues?.docNumber, initialValues?.docType]);

  const allowedTenantPks = (tenants?.list || []).map((t) => Number(t.tenant_pk));

  // MV2-1952: o e-mail só fica editável quando o atual está fora do formato, para o CS
  // corrigir. Válido, fica travado como antes (o backend também recusa a troca). O próprio
  // e-mail também fica travado: a sessão é achada pelo e-mail e trocá-lo aqui derrubaria o login.
  // MV2-2007: admin com DASH_USER_EDIT_EMAIL_PAGBANK pode editar livremente clientes PagBank.
  const loggedUserId = (() => {
    try {
      return JSON.parse(localStorage.getItem('auth-user') || '{}')?.id;
    } catch {
      return undefined;
    }
  })();
  const isOwnUser = userPk != null && String(loggedUserId) === String(userPk);
  const canEditPagBankEmail = canEditEmailPagBank(isPagBank);
  const canEditEmail =
    !isOwnUser && (canEditPagBankEmail || !isValidEmail(initialValues?.email));

  const currentUserGroupPk = Number(initialValues?.userGroupPk);

  const visibleUserGroups =
    allowedTenantPks.length > 0
      ? (userGroups?.userGroupList || []).filter(
          (group) =>
            allowedTenantPks.includes(Number(group.tenantCreatorPk)) ||
            Number(group.userGroupPk) === currentUserGroupPk,
        )
      : userGroups?.userGroupList || [];

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validate={(values) => {
        const errors = {};
        if (!values.firstName)
          errors.firstName = T('The Name field is mandatory.');
        // MV2-1952: o formato só é cobrado quando o e-mail muda, para o CS conseguir salvar
        // outros campos de um usuário antigo com e-mail fora do padrão (o backend faz igual).
        const emailChanged =
          normalizeEmail(values.email).toLowerCase() !==
          normalizeEmail(initialValues.email).toLowerCase();
        if (!isVehicle && !normalizeEmail(values.email))
          errors.email = T('The E-mail field is mandatory.');
        else if (!isVehicle && emailChanged && !isValidEmail(values.email))
          errors.email = T(INVALID_EMAIL_MESSAGE);
        // if (!/^\d+$/.test(values.phone)) errors.phone = 'O campo de telefone deve conter apenas números.'
        // if (!moment(values.dateBirthDay).isValid()) errors.dateBirthDay = 'Informe uma data válida. A data deve ser inserida no formato "AAAA-MM-DD".'
        // if (!/^[\d-]{9}$/.test(values.zipCode)) errors.zipCode = 'O campo de CEP deve conter o formato "XXXXX-XXX".'
        // if (!/^\d+$/.test(values.houseNumber)) errors.houseNumber = 'O campo de número da casa deve conter apenas números.'
        // if (!/^[a-zA-Z\u00C0-\u00ff ]+$/.test(values.country)) errors.country = 'O campo país deve conter apenas letras.'
        // if (!/^[a-zA-Z\u00C0-\u00ff ]+$/.test(values.city)) errors.city = 'O campo cidade deve conter apenas letras.'

        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(async () => {
          let data = null;
          let addressData = null;
          if (values.docNumber == null || values.docNumber == '') {
            values.docType = null;
          }
          if (type === 'create') {
          } else if (type === 'update') {
            values.ocppTags = targetKeys;

            values.allowed_chargeboxes = chargeboxKeys;

            data = await updateUserDetail(
              {
                ...values,
                company_uuid: values?.company?.uuid,
                email: isVehicle ? values.email : normalizeEmail(values.email),
              },
              userPk,
            );

            if (values.addressPk !== null && values.addressPk !== undefined) {
              addressData = await updateUserAddress(values, userPk);
            }
          }

          if (data !== null) {
            if (data.type === 'success') {
              if (type === 'create') {
                message.success('Usuário criado com sucesso!');
              } else {
                message.success('Usuário alterado com sucesso!');
              }
              setTimeout(() => {
                history.goBack();
              }, 1000);
            } else {
              message.error(
                data?.data?.error ||
                  'Não foi possível realizar a operação. Certifique-se de que os campos foram corretamente preenchidos.',
              );
            }
          } else {
            message.error(
              'Não foi possível realizar a operação. Certifique-se de que os campos foram corretamente preenchidos.',
            );
          }
          setSubmitting(false);
        }, 400);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isSubmitting,
      }) => {
        return (
          <UserConfigFormWrapper>
            <form onSubmit={handleSubmit}>
              <FormInputGroupWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('Name')}:</FormLabel>
                  <Input
                    name='firstName'
                    label='firstName'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.firstName}
                    required
                    maxLength={20}
                    allowClear
                  />
                  <ErrorMessage name='firstName' component={MessageBox} />
                </FormInputWrapper>
                {!isVehicle && (
                  <FormInputWrapper>
                    <FormLabel>E-mail:</FormLabel>
                    <Input
                      name='email'
                      label='email'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      required
                      maxLength={80}
                      allowClear
                      disabled={!canEditEmail}
                    />
                    <ErrorMessage name='email' component={MessageBox} />
                  </FormInputWrapper>
                )}
              </FormInputGroupWrapper>
              <FormInputGroupWrapper>
                {!isVehicle && (
                  <FormInputWrapper>
                    <FormLabel>{T('Phone')}:</FormLabel>
                    <Input
                      name='phone'
                      label='phone'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone}
                      required
                      maxLength={40}
                      allowClear
                    />
                    <ErrorMessage name='phone' component={MessageBox} />
                  </FormInputWrapper>
                )}

                {isVehicle && (
                  <FormInputWrapper>
                    <FormLabel>{T('Company')}</FormLabel>

                    <CompanySelect
                      history={history}
                      values={values}
                      userPk={userPk}
                      onOptionClick={(item) => setFieldValue('company', item)}
                      onChange={(value) => setFieldValue('company', value)}
                      companiesList={companiesList}
                      getCompaniesData={getCompaniesData}
                    />

                    {errors.company && (
                      <MessageBox>{errors.company}</MessageBox>
                    )}
                  </FormInputWrapper>
                )}

                <FormInputWrapper>
                  <FormLabel>OCPP Id Tag:</FormLabel>
                  <Input
                    name='ocppIdTag'
                    label='ocppIdTag'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.ocppIdTag}
                    disabled
                    maxLength={30}
                  />
                  <ErrorMessage name='ocppIdTag' component={MessageBox} />
                </FormInputWrapper>
              </FormInputGroupWrapper>

              {docTypeList.length > 0 && !!values.docNumber && (
                <FormInputGroupWrapper>
                  <FormInputWrapper>
                    <FormLabel>Tipo de Documento:</FormLabel>
                    <select
                      name='docType'
                      value={values.docType}
                      onChange={(e) => setFieldValue('docType', e.target.value)}
                      onBlur={handleBlur}
                    >
                      {docTypeList.map((docType) => (
                        <option key={docType} value={docType === 'FO' ? 'FOREIGN' : docType}>
                          {docType}
                        </option>
                      ))}
                    </select>
                  </FormInputWrapper>
                  <FormInputWrapper />
                </FormInputGroupWrapper>
              )}

              {!!values.docType ? (
                <FormInputGroupWrapper>
                  <FormInputWrapper>
                    <FormLabel>{values.docType === 'FOREIGN' || values.docType === 'FO' ? 'FO' : values.docType}:</FormLabel>
                    {values.docType === 'CPF' ? (
                      <Input
                        name='docNumber'
                        label='docNumber'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={cpfMask(values.docNumber || '')}
                        disabled
                      />
                    ) : (
                      <Input
                        name='docNumber'
                        label='docNumber'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.docNumber || ''}
                        disabled
                      />
                    )}
                    <ErrorMessage name='docNumber' component={MessageBox} />
                  </FormInputWrapper>
                  <FormInputWrapper />
                </FormInputGroupWrapper>
              ) : (
                <></>
              )}

              {values.addressPk !== null ? (
                <FormInputGroupWrapper>
                  <FormInputWrapper>
                    <FormLabel>{T('Block')}:</FormLabel>
                    <Input
                      name='block'
                      label='block'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.block}
                    />
                    <ErrorMessage name='block' component={MessageBox} />
                  </FormInputWrapper>
                  <FormInputWrapper>
                    <FormLabel>{T('Apartment')}:</FormLabel>
                    <Input
                      name='apartment'
                      label='apartment'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.apartment}
                    />
                    <ErrorMessage name='apartment' component={MessageBox} />
                  </FormInputWrapper>
                </FormInputGroupWrapper>
              ) : (
                <></>
              )}

              {values.externalId !== null ? (
                <FormInputGroupWrapper>
                  <FormInputWrapper>
                    <FormLabel>{T('External ID')}:</FormLabel>
                    <Input
                      name='externalId'
                      label='externalId'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.externalId}
                    />
                    <ErrorMessage name='externalId' component={MessageBox} />
                  </FormInputWrapper>
                  <FormInputWrapper />
                </FormInputGroupWrapper>
              ) : (
                <></>
              )}

              <FormInputGroupWrapper style={{ display: 'none' }}>
                <FormInputWrapper>
                  <FormLabel>{T('Birthday')}:</FormLabel>
                  <Input
                    name='dateBirthDay'
                    label='dateBirthDay'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.dateBirthDay}
                    maxLength={20}
                    allowClear
                  />
                  {/* <DatePicker name="dateBirthDay" label="dateBirthDay" onChange={handleDateChange} value={moment(values.dateBirthDay)}/> */}
                  <ErrorMessage name='dateBirthDay' component={MessageBox} />
                </FormInputWrapper>
              </FormInputGroupWrapper>
              <FormInputGroupWrapper style={{ display: 'none' }}>
                <FormInputWrapper>
                  <FormLabel>{T('Country')}:</FormLabel>
                  <Input
                    name='country'
                    label='country'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.country}
                    maxLength={20}
                    allowClear
                  />
                  <ErrorMessage name='country' component={MessageBox} />
                </FormInputWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('State')}:</FormLabel>
                  <Input
                    name='state'
                    label='state'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.state}
                    maxLength={20}
                    allowClear
                  />
                  <ErrorMessage name='state' component={MessageBox} />
                </FormInputWrapper>
              </FormInputGroupWrapper>
              <FormInputGroupWrapper style={{ display: 'none' }}>
                <FormInputWrapper>
                  <FormLabel>{T('City')}:</FormLabel>
                  <Input
                    name='city'
                    label='city'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.city}
                    required
                    maxLength={20}
                    allowClear
                  />
                  <ErrorMessage name='city' component={MessageBox} />
                </FormInputWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('Zip code')}:</FormLabel>
                  <Input
                    name='zipCode'
                    label='zipCode'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.zipCode}
                    maxLength={20}
                    allowClear
                  />
                  <ErrorMessage name='zipCode' component={MessageBox} />
                </FormInputWrapper>
              </FormInputGroupWrapper>
              <FormInputGroupWrapper style={{ display: 'none' }}>
                <FormInputWrapper>
                  <FormLabel>{T('House number')}:</FormLabel>
                  <Input
                    name='houseNumber'
                    label='houseNumber'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.houseNumber}
                    required
                    maxLength={20}
                    allowClear
                  />
                  <ErrorMessage name='houseNumber' component={MessageBox} />
                </FormInputWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('Street')}:</FormLabel>
                  <Input
                    name='street'
                    label='street'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.street}
                    required
                    maxLength={20}
                    allowClear
                  />
                  <ErrorMessage name='street' component={MessageBox} />
                </FormInputWrapper>
              </FormInputGroupWrapper>

              <FormInputGroupWrapper>
                <FormInputWrapper>
                  <FormLabel>{T('Account')}:</FormLabel>
                  <InputField
                    type='number'
                    as='select'
                    name='tenant_pk'
                    label='tenant_pk'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.tenant_pk}
                    required
                    maxLength={20}
                    allowClear
                  >
                    {tenants?.list?.length &&
                      tenants?.list?.map(({ tenant_pk, name }) => (
                        <option value={tenant_pk}>
                          {tenant_pk} - {name}
                        </option>
                      ))}
                  </InputField>
                  <ErrorMessage name='tenant_pk' component={MessageBox} />
                </FormInputWrapper>

                {!isVehicle && (
                  <FormInputWrapper>
                    <FormLabel className='userGroupLabel'>
                      {T('User Group')}:
                    </FormLabel>
                    <InputField
                      className='userGroupInputField'
                      type='number'
                      as='select'
                      name='userGroupPk'
                      label='userGroupPk'
                      onChange={(e) =>
                        setFieldValue('userGroupPk', Number(e.target.value))
                      }
                      onBlur={handleBlur}
                      value={values.userGroupPk}
                      required
                      maxLength={20}
                      allowClear
                    >
                      {visibleUserGroups?.length > 0 &&
                        visibleUserGroups.map(({ userGroupPk, name }) => (
                          <option value={userGroupPk}>
                            {userGroupPk} - {name}
                          </option>
                        ))}
                    </InputField>
                    <ErrorMessage name='userGroupPk' component={MessageBox} />
                  </FormInputWrapper>
                )}
              </FormInputGroupWrapper>

              <FormInputWrapper>
                <FormLabel>{T('Ocpp Tags')}:</FormLabel>
                <TagsTransfer
                  dataSource={availableOcppTags}
                  targetKeys={targetKeys}
                  setTargetKeys={setTargetKeys}
                />
              </FormInputWrapper>

              {isVehicle && (
                <FormInputWrapper>
                  <FormLabel>{T('Chargeboxes')}:</FormLabel>
                  <OperationsTransfer
                    dataSource={{
                      cpList:
                        chargePointsList.chargePointsList.map((cp) => ({
                          ...cp,
                          chargeBoxDescription: cp.description,
                        })) || [],
                    }}
                    targetKeys={chargeboxKeys}
                    setTargetKeys={setChargeboxKeys}
                    maxHeight={300}
                  />
                </FormInputWrapper>
              )}

              <CheckBoxDiv>
                <FormLabel>{T('User Active')}:</FormLabel>
                <Checkbox
                  name='activeUser'
                  label='activeUser'
                  as='select'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  checked={values.activeUser}
                />
                <ErrorMessage name='activeUser' component={MessageBox} />

                {!hideTrustedUserButton() && !isVehicle && (
                  <CheckBoxThreeDSContainer>
                    <Tooltip
                      title={T(
                        'By selecting this option, the system will skip credit card authenticity verification for this user.',
                      )}
                    >
                      <TooltipPositionWrapper>
                        <StyledQuestionIconWrapper>
                          <QuestionIcon size={15} color={COLORS.primary} />
                        </StyledQuestionIconWrapper>
                        <FormLabel>{T('Trusted user')}:</FormLabel>
                      </TooltipPositionWrapper>
                    </Tooltip>

                    <Checkbox
                      name='skipThreeDSVerification'
                      label='skipThreeDSVerification'
                      as='select'
                      onChange={(e) => {
                        if (e.target.checked) {
                          confirmTrustedUser(() =>
                            setFieldValue('skipThreeDSVerification', true),
                          );
                        } else {
                          handleChange(e);
                        }
                      }}
                      onBlur={handleBlur}
                      checked={values.skipThreeDSVerification}
                    />
                    <ErrorMessage
                      name='skipThreeDSVerification'
                      component={MessageBox}
                    />
                    <RiskScoreBadge
                      label='User'
                      loading={scoreState.loading}
                      data={scoreState.data}
                    />
                    <CardRiskScoreBadge
                      loading={cardScoreState.loading}
                      hasCard={cardScoreState.hasCard}
                      data={cardScoreState.data}
                    />
                  </CheckBoxThreeDSContainer>
                )}
              </CheckBoxDiv>

              {type === 'update' && userPk && (
                <TrustedUserAuditHistory userPk={userPk} />
              )}

              {shouldShowNotificationOfflineCp && (
                <CheckBoxDiv>
                  <FormLabel>{T('Offline Stations Notification')}:</FormLabel>
                  <Checkbox
                    name='notificationOfflineCp'
                    label='notificationOfflineCp'
                    as='select'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    checked={values.notificationOfflineCp}
                  />
                  <ErrorMessage
                    name='notificationOfflineCp'
                    component={MessageBox}
                  />
                </CheckBoxDiv>
              )}

              <Button
                type='primary'
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {T('Submit')}
              </Button>
            </form>
          </UserConfigFormWrapper>
        );
      }}
    </Formik>
  );
};

export default UserConfigForm;
